import { useState, useEffect, useRef } from 'react';
import { SWISS_POSTAL_CODES, type PostalCodeData } from '@bettermatch/shared';

// Convert postal codes object to array for searching
const postalCodeList = Object.values(SWISS_POSTAL_CODES);

interface PostalCodeAutocompleteProps {
  value: string;
  onChange: (plz: string, canton: string, cityName: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
}

export function PostalCodeAutocomplete({
  value,
  onChange,
  onBlur,
  disabled,
  error,
  label = 'Postal Code',
  required,
}: PostalCodeAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<PostalCodeData[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Sync query with value when it changes externally
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Filter suggestions when query changes
  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    const queryLower = query.toLowerCase();
    const filtered = postalCodeList
      .filter(
        (item) =>
          item.plz.startsWith(query) || item.name.toLowerCase().includes(queryLower)
      )
      .slice(0, 10); // Limit to 10 suggestions

    setSuggestions(filtered);
    setHighlightedIndex(0);
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (suggestions[highlightedIndex]) {
          selectItem(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const selectItem = (item: PostalCodeData) => {
    setQuery(item.plz);
    onChange(item.plz, item.canton, item.name);
    setIsOpen(false);
    setSuggestions([]);
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && suggestions.length > 0) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, suggestions.length]);

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => !disabled && setIsOpen(true)}
        onBlur={() => {
          // Delay close to allow click on suggestion
          setTimeout(() => setIsOpen(false), 150);
          onBlur?.();
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search PLZ or city..."
        disabled={disabled}
        autoComplete="off"
        className={`mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 ${error ? 'border-red-300' : ''}`}
      />
      {isOpen && suggestions.length > 0 && !disabled && (
        <div
          ref={listRef}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {suggestions.map((item, idx) => (
            <div
              key={item.plz}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur from firing
                selectItem(item);
              }}
              className={`flex cursor-pointer items-center justify-between px-3 py-2 text-sm ${
                idx === highlightedIndex ? 'bg-primary-50' : 'hover:bg-gray-50'
              }`}
            >
              <div>
                <span className="font-medium text-gray-900">{item.plz}</span>
                <span className="ml-2 text-gray-600">{item.name}</span>
              </div>
              <span className="rounded bg-primary-100 px-1.5 py-0.5 text-xs font-medium text-primary-700">
                {item.canton}
              </span>
            </div>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
